import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import Button from "../../Buttons/Button";

const CustomSearchNew = ({
  value,
  onChange,
  onFilteredCountChange,
  names = [],
  onSelect,
  searchType,
  onSearch,
  showAddButton = false,
  // onKeyDown,
  toggleAddAccountModal,
  setIsEmptyGoogleResponse,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownContainerRef = useRef(null);

  // Use the prop directly instead of internal state
  const searchValue = value || "";

  // Fix: Handle both string and object arrays properly
  const filteredNames = React.useMemo(() => {
    if (!searchValue || searchValue.length === 0) {
      return [];
    }

    // If names is empty, return empty array
    if (!names || names.length === 0) {
      return [];
    }

    // Handle mixed array of strings and objects
    return names
      .filter((item) => {
        if (typeof item === "string") {
          return item.toLowerCase().includes(searchValue.toLowerCase());
        } else if (item && typeof item === "object") {
          // Handle objects with name, email, or other searchable properties
          let searchableText = "";
          if (searchType === "Company" || searchType === "Domain") {
            searchableText = `${item.name || ""} ${item.domain || ""}`;
          } else if (searchType === "Email") {
            searchableText = item.email?.email || "";
          }
          return searchableText
            .toLowerCase()
            .includes(searchValue.toLowerCase().trim());
        }
        return false;
      })
      .slice(0, 100); // Limit to 10 results for better UX
  }, [names, searchValue]);

  // Notify parent about filtered count changes
  useEffect(() => {
    if (onFilteredCountChange) {
      onFilteredCountChange(filteredNames.length);
    }
  }, [filteredNames.length, onFilteredCountChange]);

  // Handle input change
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setIsOpen(inputValue.length > 0 && filteredNames.length > 0);
    setHighlightedIndex(-1);

    // Call the parent's onChange
    if (onChange) {
      onChange(e);
    }
  };

  // Fix: Update handleSelect to call onSelect prop
  const handleSelect = (item) => {
    setIsOpen(false);
    setHighlightedIndex(-1);

    // First call the onSelect prop if it exists
    if (onSelect) {
      onSelect(item);
      return; // Let the parent handle the value setting
    }

    if (onChange) {
      const syntheticEvent = {
        target: {
          value: item,
          originalItem: item,
        },
      };
      onChange(syntheticEvent);
    }
  };

  const handleKeyDown = (e) => {
    if (!isOpen || filteredNames.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredNames.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredNames.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredNames[highlightedIndex]) {
          handleSelect(filteredNames[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (searchValue.length > 0) {
      setIsOpen(filteredNames.length > 0);
    } else {
      setIsOpen(false);
    }
  }, [filteredNames.length, searchValue]);

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current && dropdownContainerRef.current) {
      const updatePosition = () => {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownRect =
          dropdownContainerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Calculate available space
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        // Determine if dropdown should be below or above button
        const showBelow =
          spaceBelow >= Math.min(320, dropdownRect.height) ||
          spaceBelow >= spaceAbove;

        // Calculate left position
        let left = buttonRect.left;
        // Adjust if dropdown would overflow right edge
        if (left + dropdownRect.width > viewportWidth) {
          left = Math.max(0, viewportWidth - dropdownRect.width);
        }

        // Calculate top position
        let top;
        if (showBelow) {
          top = buttonRect.bottom + 8; // Add 8px gap
          // Check if dropdown extends beyond bottom of viewport
          if (top + dropdownRect.height > viewportHeight) {
            top = viewportHeight - dropdownRect.height - 8;
          }
        } else {
          top = buttonRect.top - dropdownRect.height - 8; // Add 8px gap
          // Check if dropdown extends beyond top of viewport
          if (top < 0) {
            top = 8;
          }
        }

        // Apply the calculated position
        dropdownContainerRef.current.style.left = `${left}px`;
        dropdownContainerRef.current.style.top = `${top}px`;
      };

      // Initial position update
      updatePosition();

      // Update position on scroll
      const handleScroll = () => {
        requestAnimationFrame(updatePosition);
      };

      // Add scroll event listeners
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);

      // Cleanup
      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is inside the dropdownRef (button) or the dropdownContainerRef (portal dropdown)
      if (
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (dropdownContainerRef.current &&
          dropdownContainerRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
      setHighlightedIndex(-1);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper function to get display text for an item
  const getDisplayText = (item) => {
    if (typeof item === "string") return item;
    return item.name || item.email?.email || item.domain || "";
  };


  return (
    <div className="w-full flex justify-center my-10">
      <div className="w-full max-w-[500px] relative" ref={dropdownRef}>
        <div
          className="rounded-full bg-primary-500 p-2 relative"
          ref={buttonRef}
        >
          <div
            className="bg-white rounded-full"
            style={{ boxShadow: "0px 0px 6px 6px rgba(255, 255, 255, 1)" }}
          >
            <input
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              // onKeyDown={(e) => {
              //   onKeyDown && onKeyDown(e);
              //   handleKeyDown(e);
              // }}
              placeholder="Search by Account Name or Domain or Email ID"
              className="w-full h-full px-4 py-2 text-base text-black placeholder:text-black outline-none rounded-full bg-transparent"
            />
          </div>

          <button className="absolute right-0 top-1/2 -translate-y-1/2 w-16 h-16 rounded-full flex justify-center items-center cursor-pointer" onClick={() => onSearch?.()}>
            {/* Blurred background layer */}
            <div
              className="absolute inset-0 bg-primary-500 rounded-full"
              style={{ filter: "blur(2px)" }}
            ></div>
            {/* Sharp content layer */}
            <div className="relative z-10 bg-primary-500 w-14 h-14 rounded-full flex justify-center items-center cursor-pointer">
              <span className="material-symbols-outlined text-lg text-white">
                search
              </span>
            </div>
          </button>
        </div>

        {/* Dropdown */}
        {isOpen &&
          filteredNames.length > 0 &&
          createPortal(
            <div
              ref={dropdownContainerRef}
              style={{
                position: "fixed",
                width: buttonRef.current?.offsetWidth,
              }}
              className="z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-2"
            >
              {filteredNames.map((item, index) => {
                const displayText = getDisplayText(item);
                const itemKey =
                  typeof item === "string"
                    ? item
                    : item._id || item.uiid || item.name || index;

                return (
                  <div
                    key={itemKey}
                    className={`flex items-center gap-1 px-4 py-2 cursor-pointer transition-colors ${
                      index === highlightedIndex
                        ? "bg-primary text-white"
                        : "hover:bg-primary-50"
                    }`}
                    onClick={() => handleSelect(item)}
                  >
                    {/* Show source icon if available */}
                    {typeof item !== "string" && item.from && (
                      <div className="text-xs text-gray-400">
                        {item.from === "linkedin" ? (
                          <svg
                            height="15px"
                            width="15px"
                            viewBox="0 0 382 382"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              style={{ fill: "#0077B7" }}
                              d="M347.445,0H34.555C15.471,0,0,15.471,0,34.555v312.889C0,366.529,15.471,382,34.555,382h312.889
                              C366.529,382,382,366.529,382,347.444V34.555C382,15.471,366.529,0,347.445,0z M118.207,329.844c0,5.554-4.502,10.056-10.056,10.056
                              H65.345c-5.554,0-10.056-4.502-10.056-10.056V150.403c0-5.554,4.502-10.056,10.056-10.056h42.806
                              c5.554,0,10.056,4.502,10.056,10.056V329.844z M86.748,123.432c-22.459,0-40.666-18.207-40.666-40.666S64.289,42.1,86.748,42.1
                              s40.666,18.207,40.666,40.666S109.208,123.432,86.748,123.432z M341.91,330.654c0,5.106-4.14,9.246-9.246,9.246H286.73
                              c-5.106,0-9.246-4.14-9.246-9.246v-84.168c0-12.556,3.683-55.021-32.813-55.021c-28.309,0-34.051,29.066-35.204,42.11v97.079
                              c0,5.106-4.139,9.246-9.246,9.246h-44.426c-5.106,0-9.246-4.14-9.246-9.246V149.593c0-5.106,4.14-9.246,9.246-9.246h44.426
                              c5.106,0,9.246,4.14,9.246,9.246v15.655c10.497-15.753,26.097-27.912,59.312-27.912c73.552,0,73.131,68.716,73.131,106.472
                              L341.91,330.654L341.91,330.654z"
                            />
                          </svg>
                        ) : (
                          <span className="material-symbols-outlined !text-[16px]">
                            database_search
                          </span>
                        )}
                      </div>
                    )}
                    <div>{displayText}</div>
                    
                  </div>
                );
              })}
              {showAddButton && 
              <div className="px-4 py-2"><Button size="xs-sm" title={"Add"} onClick={()=>{
                toggleAddAccountModal(true);
              }}/></div>
               }
            </div>,
            document.querySelector(".select-root") || document.body
          )}
      </div>
    </div>
  );
};

export default CustomSearchNew;
